import _ from 'lodash';
import {
  ediVariableNames,
  EntryType,
  reservedNames,
  ReservedNodeType,
  isTreeMap,
  TreeItem,
  TreeMap,
  TreeNode,
} from '@morph-mapper/types';
import { getEntries } from '@morph-mapper/utils';
import { match, P } from 'ts-pattern';
import { err, ok, Result } from 'neverthrow';
import {
  Reference,
  ReferenceParseError,
  SearchResultType,
  SearchResult,
  ReferenceResolveType,
  ReferenceResolveResult,
} from './types';

/**
 * Recursively searches through an object to find all string values that match a given regular expression pattern.
 *
 * @param obj - The object to search through. Can be of any type.
 * @param pattern - A regular expression used to test string values within the object.
 * @returns An array of strings that match the provided pattern.
 *
 * @example
 * ```typescript
 * const data = {
 *   name: "John Doe",
 *   details: {
 *     email: "john.doe@example.com",
 *     phone: "123-456-7890"
 *   },
 *   tags: ["developer", "typescript"]
 * };
 *
 * const pattern = /\d{3}-\d{3}-\d{4}/; // Matches phone numbers
 * const result = findValuesWithPattern(data, pattern);
 * console.log(result); // Output: ["123-456-7890"]
 * ```
 */
const findValuesWithPattern = (obj: unknown, pattern: RegExp) => {
  const results: string[] = [];

  const search = (obj: unknown, pattern: RegExp) => {
    _.forOwn(obj, (value) => {
      if (_.isObject(value)) {
        search(value, pattern);
      } else if (_.isString(value) && pattern.test(value)) {
        results.push(value);
      }
    });
  };

  search(obj, pattern);

  return results;
};

/**
 * Recursively searches through an object to find all - keys - or string values that match a given regular expression pattern.
 *
 * @param obj - The object to search through. Can be of any type.
 * @param pattern - A regular expression used to test keys and string values within the object.
 * @returns An array of strings that match the provided pattern, including both keys and values.
 */
const findKeyOrValueWithPattern = (obj: unknown, pattern: RegExp) => {
  const results: string[] = [];

  const search = (obj: unknown, pattern: RegExp) => {
    _.forOwn(obj, (value, key) => {
      // Check the key
      if (pattern.test(key)) {
        results.push(key);
      }

      // Check the value
      if (_.isObject(value)) {
        search(value, pattern);
      } else if (_.isString(value) && pattern.test(value)) {
        results.push(value);
      }
    });
  };

  search(obj, pattern);

  return results;
};

const findValuesForKey = (obj: unknown, key: string) => {
  const results: string[] = [];

  const search = (obj: unknown) => {
    _.forOwn(obj, (value, currentKey) => {
      if (currentKey === key && _.isString(value)) {
        results.push(value);
      }

      if (_.isObject(value)) {
        search(value);
      }
    });
  };

  search(obj);

  return results;
};

const searchSiblings = (parent: TreeMap, variable: string) => {
  for (const [id, name] of getEntries(parent.children.forwardMap)) {
    if (name === variable) {
      return id;
    }
  }
};

/**
 * This function parses an incoming string with an expected format of `anchor.path1.path2...`.
 * It splits the string into an object with two properties: `anchor` and `path`.
 *
 * @param value - The string value to be parsed into a reference.
 * @returns
 */
const parseValueToReference = (
  value: string,
): Result<Reference, ReferenceParseError> => {
  const [anchor, ...path] = value.split('.');

  if (anchor === '' || path.some((el) => el === ''))
    return err(ReferenceParseError.InvalidPath);

  return ok({
    anchor,
    path,
  });
};

// Limitations: The final $data declaration must be a map type.
// TODO: refactor to support an interface instead of exposing the data structure directly.
// TODO: support composite data references
export const createTemplateReferencesFinder = (
  entries: Record<string, TreeNode>,
) => {
  const findNearestIterator = (
    entryId: string,
  ): Result<string, ReferenceParseError> => {
    const entry = entries[entryId];
    if (entry.parentId === undefined) {
      return err(ReferenceParseError.DeclarationNotFound);
    }

    const parent = entries[entry.parentId];
    if (!isTreeMap(parent)) {
      return err(ReferenceParseError.IsMapType);
    }

    for (const [id, name] of getEntries(parent.children.forwardMap)) {
      if (name === ReservedNodeType.Iterator) {
        return ok(id);
      }
    }

    return findNearestIterator(entry.parentId);
  };

  /**
   * We recursively search for $declareVariables available in the parent tree.
   * If we find a $declareVariable, we search the children for the variable declaration.
   *
   * @param entryId current parent entry id
   * @param variable target variable to find
   * @returnn the entry id of the variable declaration
   * @throws if the variable declaration is not found in the tree
   */
  const findVariableInDeclare = (
    entryId: string,
    reference: Reference,
  ): Result<string | undefined, ReferenceParseError> => {
    const { anchor } = reference;
    let declarationId = undefined;

    const entry = entries[entryId];
    if (entry.parentId === undefined) {
      return err(ReferenceParseError.DeclarationNotFound);
    }

    const parent = entries[entry.parentId];
    if (!isTreeMap(parent)) {
      return err(ReferenceParseError.IsMapType);
    }

    if (parent.key === ReservedNodeType.DeclareVariable) {
      declarationId = searchSiblings(parent, anchor);
    }

    const childId =
      parent.children.reverseMap[ReservedNodeType.DeclareVariable];
    if (childId !== undefined) {
      const entry = entries[childId];
      if (!isTreeMap(entry)) {
        return err(ReferenceParseError.IsMapType);
      }
      declarationId = searchSiblings(entry, anchor);
    }

    if (declarationId !== undefined) {
      return ok(declarationId);
    }

    declarationId = findVariableInDeclare(entry.parentId, reference);
    if (declarationId.isErr()) {
      return err(declarationId.error);
    }

    return ok(declarationId.value);
  };

  /**
   * We recursively search for $data available in the parent tree.
   *
   * @param entryId current parent entry id
   * @returns the entry id of the nearest $data
   * @throws if the $data declaration is not found in the tree
   */
  const findNearestData = (
    entryId: string,
  ): Result<string, ReferenceParseError> => {
    const entry = entries[entryId];
    if (entry.parentId === undefined) {
      return err(ReferenceParseError.DeclarationNotFound);
    }

    const parent = entries[entry.parentId];
    if (!isTreeMap(parent)) {
      return err(ReferenceParseError.IsMapType);
    }

    for (const [id, name] of getEntries(parent.children.forwardMap)) {
      if (['data', '$data'].includes(name) && id !== entryId) {
        return ok(id);
      }
    }

    return findNearestData(entry.parentId);
  };

  /**
   *
   *
   * @param entryId
   * @param variable
   * @returns
   */
  const findDeclarationInData = (
    entryId: string,
    variable: string,
  ): Result<string, ReferenceParseError> => {
    const data = entries[entryId];
    if (!isTreeMap(data)) {
      return err(ReferenceParseError.IsMapType);
    }

    for (const [id, name] of getEntries(data.children.forwardMap)) {
      if (name === variable) {
        return ok(id);
      }
    }

    for (const [id, name] of getEntries(data.children.forwardMap)) {
      if (['data', '$data'].includes(name)) {
        return findDeclarationInData(id, variable);
      }
    }

    return err(ReferenceParseError.DeclarationNotFound);
  };

  const findVariableInData = (
    entryId: string,
    variable: string,
  ): Result<string, ReferenceParseError> => {
    const dataId = findNearestData(entryId);
    if (dataId.isErr()) {
      return err(dataId.error);
    }
    // Determine the variable we are looking for.
    const variableElements = variable.split('.');
    if (variableElements.length === 1) {
      return dataId;
    }

    // If the found $data is not a map we need to resolve the $data definition recursively until we find the $data which contains declarations.
    const targetDataId = findNearestData(entryId).map((id) => {
      if (entries[id].type === EntryType.Map) {
        return id;
      }

      const declarationIds: string[] = [];
      for (const result of resolveReferences(id)) {
        // TODO: Are we ignoring the errors?
        if (result.type === ReferenceResolveType.Found) {
          declarationIds.push(result.id);
        }
      }

      return declarationIds[0];
    });

    const parsedVariable = variableElements.slice(1).find((v) => isNaN(+v));
    if (parsedVariable === undefined) {
      return err(ReferenceParseError.DeclarationNotFound);
    }

    const declarationId = targetDataId.andThen((id) =>
      findDeclarationInData(id, parsedVariable),
    );
    if (declarationId.isErr()) {
      return err(declarationId.error);
    }

    return ok(declarationId.value);
  };

  /**
   * Finds the declaration ID for a given variable within the context of a specific entry.
   *
   * @param {string} entryId - The ID of the entry in which the variable is being searched.
   * @param {string} variable - The variable whose declaration ID needs to be resolved.
   * @returns {string | undefined} - The ID of the resolved declaration, or `undefined` if no valid declaration is found.
   */
  const findVariableDeclaration = (
    entryId: string,
    variable: string,
  ): Result<SearchResult, ReferenceParseError> => {
    const entry = entries[entryId];
    if (entry.type === EntryType.Map) {
      return err(ReferenceParseError.IsMapType);
    }
    if (reservedNames.has(variable) || ediVariableNames.has(variable)) {
      return ok({
        type: SearchResultType.Skipped,
      });
    }

    const reference = parseValueToReference(variable);
    if (reference.isErr()) {
      return err(reference.error);
    }
    if (reference.value.anchor === 'inputdocument') {
      return ok({
        type: SearchResultType.Skipped,
      });
    }

    const declarationId = match(variable)
      .with(P.string.regex(/^data/), () =>
        findVariableInData(entryId, variable),
      )
      .otherwise(() => findVariableInDeclare(entryId, reference.value));

    if (declarationId.isErr()) {
      return err(declarationId.error);
    }
    if (declarationId.value === undefined) {
      return err(ReferenceParseError.DeclarationNotFound);
    }

    return ok({
      type: SearchResultType.Found,
      id: declarationId.value,
    });
  };

  const resolveObjectReferences = (
    entryId: string,
    entry: TreeItem,
  ): ReferenceResolveResult[] => {
    if (typeof entry.value !== 'object') {
      // Nothing to resolve if the value is not an object.
      return [];
    }

    const result: ReferenceResolveResult[] = [];

    findValuesWithPattern(entry.value, /^\$/).forEach((v) => {
      findVariableDeclaration(entryId, v.slice(1)).match(
        (searchResult) =>
          match(searchResult)
            .with({ type: SearchResultType.Found }, ({ id }) => {
              result.push({
                type: ReferenceResolveType.Found,
                id,
              });
            })
            .with({ type: SearchResultType.Skipped }, () => {
              result.push({
                type: ReferenceResolveType.Skipped,
              });
            })
            .exhaustive(),
        (error) =>
          result.push({
            type: ReferenceResolveType.Errored,
            error,
          }),
      );
    });

    findValuesForKey(entry.value, '$getVariable').forEach((v) => {
      findVariableDeclaration(entryId, v).match(
        (searchResult) =>
          match(searchResult)
            .with({ type: SearchResultType.Found }, ({ id }) => {
              result.push({
                type: ReferenceResolveType.Found,
                id,
              });
            })
            .with({ type: SearchResultType.Skipped }, () => {
              result.push({
                type: ReferenceResolveType.Skipped,
              });
            })
            .exhaustive(),
        (error) =>
          match(error)
            .with(ReferenceParseError.DeclarationNotFound, () => {
              result.push({
                type: ReferenceResolveType.Skipped,
              });
            })
            .otherwise(() => {
              result.push({
                type: ReferenceResolveType.Errored,
                error,
              });
            }),
      );
    });

    if (findKeyOrValueWithPattern(entry.value, /^#>/).length === 0) {
      return result;
    }

    findNearestIterator(entryId).match(
      (iteratorId) =>
        result.push({
          type: ReferenceResolveType.Found,
          id: iteratorId,
        }),
      (error) =>
        result.push({
          type: ReferenceResolveType.Errored,
          error,
        }),
    );

    return result;
  };

  const resolveStringReferences = (
    entryId: string,
    entry: TreeItem,
  ): ReferenceResolveResult[] => {
    if (typeof entry.value !== 'string') {
      // Nothing to resolve if the value is not a string.
      return [];
    }

    const result: ReferenceResolveResult[] = [];

    match(entry.value)
      .with(P.string.regex(/^\$/), (value) => {
        findVariableDeclaration(entryId, value.slice(1)).match(
          (searchResult) =>
            match(searchResult)
              .with({ type: SearchResultType.Found }, ({ id }) => {
                result.push({
                  type: ReferenceResolveType.Found,
                  id,
                });
              })
              .with({ type: SearchResultType.Skipped }, () => {
                result.push({
                  type: ReferenceResolveType.Skipped,
                });
              })
              .exhaustive(),
          (error) =>
            result.push({
              type: ReferenceResolveType.Errored,
              error,
            }),
        );
      })
      .with(P.string.regex(/^#>/), () => {
        findNearestIterator(entryId).match(
          (id) =>
            result.push({
              type: ReferenceResolveType.Found,
              id,
            }),
          (error) =>
            result.push({
              type: ReferenceResolveType.Errored,
              error,
            }),
        );
      })
      .with(P.string.regex(/^\[|\{/), (value) => {
        findValuesWithPattern(JSON.parse(value), /^\$/).forEach((v) => {
          findVariableDeclaration(entryId, v.slice(1)).match(
            (searchResult) =>
              match(searchResult)
                .with({ type: SearchResultType.Found }, ({ id }) => {
                  result.push({
                    type: ReferenceResolveType.Found,
                    id,
                  });
                })
                .with({ type: SearchResultType.Skipped }, () => {
                  result.push({
                    type: ReferenceResolveType.Skipped,
                  });
                })
                .exhaustive(),
            (error) =>
              result.push({
                type: ReferenceResolveType.Errored,
                error,
              }),
          );
        });

        if (findKeyOrValueWithPattern(JSON.parse(value), /^#>/).length === 0) {
          return;
        }

        findNearestIterator(entryId).match(
          (id) =>
            result.push({
              type: ReferenceResolveType.Found,
              id,
            }),
          (error) =>
            result.push({
              type: ReferenceResolveType.Errored,
              error,
            }),
        );
      })
      .otherwise(() => null);

    return result;
  };

  /**
   * Resolves references within an entry by checking for specific patterns in its value.
   *
   * @param {string} entryId - The ID of the entry whose references need to be resolved.
   *
   * @remarks
   * - Entries of type `Map` are skipped, as they cannot contain direct references.
   * - String values are checked for special patterns (`$` and `#>`) and processed accordingly.
   * - Object and array values are traversed recursively to process nested object structures.
   */
  const resolveReferences = (entryId: string): ReferenceResolveResult[] => {
    const entry = entries[entryId];
    const result: ReferenceResolveResult[] = [];

    // A map can never contain a direct reference in its definition.
    if (entry.type === EntryType.Map) {
      return [{ type: ReferenceResolveType.Skipped }];
    }

    result.push(...resolveObjectReferences(entryId, entry));
    result.push(...resolveStringReferences(entryId, entry));

    return result;
  };

  return {
    findNearestIterator,
    findVariableDeclaration,
    resolveReferences,
  };
};
